import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import UniversalNavbar from './UniversalNavbar';
import VFooter from './VFooter';
import API_BASE_URL from '../config/api';
import { PrimaryButton } from '../atelier';

import { devLog } from '../utils/devLog';
function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSuccess(false);

    if (!email) {
      setError('Please enter your email address.');
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/forgot_password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        setIsSuccess(true);
        setEmail('');
      } else {
        setError(data.error || 'Failed to send reset link. Please try again.');
      }
    } catch (error) {
      setError('An unexpected error occurred. Please check your internet connection and try again.');
      devLog.error('Forgot password error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  };

  const card = 'rounded-2xl border border-line bg-white p-6 sm:p-8 shadow-[0_20px_44px_-36px_rgba(22,19,15,0.4)]';

  return (
    <div className="min-h-screen bg-bone text-ink">
      <UniversalNavbar />

      <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md mx-auto">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {/* Header */}
            <motion.div variants={itemVariants} className="text-center mb-8">
              <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-primary mb-3">
                Password recovery
              </p>
              <h1 className="font-fell text-4xl font-semibold leading-[1.05] text-ink mb-3">
                Locked out of the studio?
              </h1>
              <p className="text-[15px] text-muted">
                No worries — enter your email and we'll send you reset instructions.
              </p>
            </motion.div>

            {/* Success State */}
            {isSuccess ? (
              <motion.div variants={itemVariants}>
                <div className={`${card} text-center`}>
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="text-green-600" size={32} />
                  </div>
                  <h3 className="font-fell text-2xl font-semibold text-ink mb-3">Check your email</h3>
                  <p className="text-[14.5px] text-muted mb-6">
                    If an account exists with that email, we've sent password reset instructions.
                    Please check your inbox and spam folder.
                  </p>
                  <Link
                    to="/login"
                    className="flex w-full items-center justify-center gap-2 rounded-full border border-line bg-white px-6 py-3 text-[15px] font-semibold text-ink transition-colors hover:border-gray-300"
                  >
                    <ArrowLeft size={18} />
                    Back to login
                  </Link>
                </div>
              </motion.div>
            ) : (
              /* Reset Form */
              <motion.div variants={itemVariants}>
                <div className={card}>
                  {error && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-[13.5px] font-semibold text-red-700"
                      role="alert"
                    >
                      {error}
                    </motion.div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label htmlFor="email" className="mb-1.5 block text-[13px] font-bold text-ink">
                        Email address
                      </label>
                      <div className="flex items-center gap-3 rounded-xl border border-line bg-white px-4 h-[52px] transition-colors focus-within:border-primary">
                        <Mail size={18} className="flex-shrink-0 text-muted" />
                        <input
                          type="email"
                          name="email"
                          id="email"
                          placeholder="you@example.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                          className="w-full bg-transparent text-[15px] text-ink outline-none placeholder:text-gray-400"
                        />
                      </div>
                      <p className="mt-1.5 text-[12.5px] text-muted">
                        Enter the email address associated with your account
                      </p>
                    </div>

                    <PrimaryButton type="submit" disabled={isLoading} className="w-full">
                      {isLoading ? (
                        <>
                          <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                          Sending reset link…
                        </>
                      ) : (
                        'Send reset link'
                      )}
                    </PrimaryButton>
                  </form>

                  <div className="mt-6 text-center">
                    <Link
                      to="/login"
                      className="inline-flex items-center text-[13.5px] font-semibold text-primary transition-opacity hover:opacity-70"
                    >
                      <ArrowLeft size={16} className="mr-2" />
                      Back to login
                    </Link>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Help Text */}
            <motion.div variants={itemVariants} className="mt-8 text-center">
              <p className="text-[13px] text-muted mb-2">Need more help?</p>
              <div className="flex flex-col sm:flex-row gap-2 justify-center text-[13px]">
                <a
                  href="mailto:team@vasstr.com"
                  className="font-semibold text-primary transition-opacity hover:opacity-70"
                >
                  team@vasstr.com
                </a>
                <span className="hidden sm:inline text-gray-400">•</span>
                <span className="text-gray-600">+91 97043 87878</span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
      <VFooter />
    </div>
  );
}

export default ForgotPassword;
